import { createApp } from 'vue'
import { getOrCreateUserUUID } from './utils/uuid'
import {
  setToken as setChannelToken,
  setAgentToken,
  setRole,
  setMemberId,
  setChannelId,
} from './utils/channelStorage'
import { createPinia } from 'pinia'
import CustomerWindow from './components/ChatWindow/CustomerWindow.vue'
import AgentWindow from './components/ChatWindow/AgentWindow.vue'
import AgentAuthError from './components/ChatWindow/AgentAuthError.vue'
// ============================================================================
// 设计令牌注入 —— 根因记录（务必保持静态 ?inline 导入，勿改回动态 import）
// ----------------------------------------------------------------------------
// 现象：作为插件嵌入第三方页面时，ChatHistoryBoundary 等组件里 color: var(--text-3)
//       与 background-color: var(--neutral-fill-1) 失效（分割线穿过文字、文字偏黑）；
//       而项目内部重新打包的测试窗口一切正常，无法复现。
// 根因：旧实现用动态 import('./design/color.less?inline') 加载令牌，Vite 会把它们拆成
//       独立分包（color-*.js / font-*.js）。widget.js 以 UMD 形式经普通 <script> 加载，
//       动态 import 的相对路径会按【宿主页的 document base URL】解析，而非 widget.js 自身
//       所在地址；嵌入第三方域名时分包请求落到宿主根目录 → 404 → 进入 catch → :host{--vars}
//       从未注入 → 无 fallback 的 CSS 变量取不到值。内部测试因 dist 与页面同源同路径，
//       分包能正常加载，所以不复现。
// 修复：改为静态 ?inline 导入，令牌字符串被打进 widget.js 主包，并在构造函数同步注入到
//       Shadow DOM（见 injectStyles），彻底消除运行时分包请求与注入时序竞态。
// ============================================================================
import fontCss from './design/font.less?inline'
import colorCss from './design/color.less?inline'
import i18nPlugin, { useI18n, t } from './i18n/simple-i18n'
import { message } from './hooks/message'
import { getChatUserInfo, getStyleSetting } from './api/simple-api'

window.useI18n = useI18n

// 深度优先查找对象树中第一个 key === targetKey 的值；未找到返回 undefined。
// 用于按后端配置的 jsTokenKey 从宿主页 localStorage 里的 JSON 中挖出访客名。
function deepFindByKey(obj, targetKey) {
  if (obj === null || typeof obj !== 'object') return undefined
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const r = deepFindByKey(item, targetKey)
      if (r !== undefined) return r
    }
    return undefined
  }
  if (Object.prototype.hasOwnProperty.call(obj, targetKey)) return obj[targetKey]
  for (const k of Object.keys(obj)) {
    const r = deepFindByKey(obj[k], targetKey)
    if (r !== undefined) return r
  }
  return undefined
}

// 按后端 styleSetting 配置从宿主页 localStorage 提取自定义访客名。
// customVisitorName 关闭或任一步失败(缺 key / 解码失败 / 解析失败 / 字段缺失) → 返回 null，
// 由调用方回退到 uuid。
function resolveVisitorNameFromLS(styleData) {
  try {
    if (!styleData?.customVisitorName) return null
    const { jsJsonKey, jsTokenKey, tokenDecode } = styleData
    if (!jsJsonKey || !jsTokenKey) return null
    const raw = localStorage.getItem(jsJsonKey)
    if (!raw) return null
    const jsonStr = tokenDecode ? atob(raw) : raw
    const parsed = JSON.parse(jsonStr)
    const found = deepFindByKey(parsed, jsTokenKey)
    if (found === undefined || found === null || found === '') return null
    return String(found)
  } catch (e) {
    console.warn('自定义访客名解析失败，回退到 uuid:', e)
    return null
  }
}

const VITE_API = import.meta.env.VITE_API_BASE_URL
const VITE_LOCAL_API = import.meta.env.VITE_API_LOCAL_URL
console.log(VITE_API)
console.log(VITE_LOCAL_API)
console.log('origin', window.location.origin)

// 定义 Web Component
class ABotWidget extends HTMLElement {
  constructor() {
    super()
    // 创建 Shadow DOM
    this.shadow = this.attachShadow({ mode: 'open' })

    // 同步注入设计令牌与工具类（:host{--vars} + .w-/.s-/.text-* 等）。
    // 字符串已随主包内联，挂载前即就位，无注入竞态、无运行时分包请求。
    this.injectStyles()

    // ✅ 关键：把样式注入到 Shadow DOM
    if (window.__ABOT_INJECT_CSS__) {
      window.__ABOT_INJECT_CSS__(this.shadow)
    }

    // 创建应用容器
    this.container = document.createElement('div')
    this.container.className = 'abot-widget-container'
    this.shadow.appendChild(this.container)

    this.configId = null
    this.app = null
  }

  injectStyles() {
    try {
      // 令牌字符串已随主包内联（见文件顶部根因记录），同步注入、无运行时分包请求。
      const styleContent = [fontCss, colorCss].join('')

      const styleElement = document.createElement('style')
      styleElement.textContent = styleContent
      this.shadow.appendChild(styleElement)
    } catch (error) {
      // 样式注入失败不应阻断挂载：宁可少样式也要保证 widget 渲染。
      console.error('❌ ABot Widget 样式注入失败:', error)
    }
  }

  connectedCallback() {
    this.configId = this.getConfigFromScript()
    this.initWidget()
  }

  // 客服模式的辨识条件：URL 查询串同时具备 token 与 chatId。
  //
  // 为什么用"两者都有"而不是单看 token：widget.js 通过 <script> 嵌入第三方页面时，
  // 宿主页 URL 里的 ?token=... 是宿主的业务参数，不能被误判为客服身份。
  // token+chatId 组合几乎不会与宿主业务冲突（现有访客侧从不依赖 chatId 参数），
  // 命中的一定是我们后端签发的客服链接：widget.html?config=X&token=Y&chatId=Z。
  //
  // config 允许缺失回退：客服链接总是带 config，但即使缺失，configId 仍会通过
  // getConfigFromScript 的 script src 兜底逻辑取到，这里就不重复校验。
  getAgentModeParams() {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('auth') // token改用auth字段拿
    const chatId = urlParams.get('chatId')
    // agentId：当前客服自己的 id（数字字符串），用于消息气泡的 isMe 判定。
    // agent 消息的 senderId 就是这个值（对齐访客侧 CustomerWindow.vue 中
    // cStore.currentAgent = latestChatData.data.agentId 的语义）。
    const agentId = urlParams.get('agentId')
    if (!token || !chatId) return null
    return { token, chatId, agentId }
  }

  getConfigFromScript() {
    // 客服独立页模式下没有嵌入的 widget.js <script> 标签，配置从 URL 的 ?config 取。
    // 这里先兜底走 URL，能取到就返回；否则走原来的 script src 解析路径。
    const fromUrl = new URLSearchParams(window.location.search).get('config')
    if (fromUrl) return fromUrl

    const scripts = document.getElementsByTagName('script')
    for (let script of scripts) {
      if (script.src && (script.src.includes('index.js') || script.src.includes('widget.js'))) {
        try {
          const url = new URL(script.src)
          return url.searchParams.get('config') || 'default'
        } catch (error) {
          console.warn('URL解析失败，使用默认config')
          return 'default'
        }
      }
    }
    return 'default'
  }

  getLocalFromURL() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('local') || 'false'
  }

  async fetchUserConfig(configId) {
    try {
      console.log('🌐 从服务器获取配置...', configId)

      const username = getOrCreateUserUUID()
      localStorage.setItem('__ABOT_PLUGIN_USERNAME__', username)

      // 这次调接口拿 tokenName 所需配置，样式数据顺带透传给 CustomerWindow 复用
      const styleResponse = await getStyleSetting(this.configId)
      const styleData = await styleResponse.json()
      console.log('✅ 样式获取成功:', styleData)
      // 自定义访客名：按后端配置从宿主页 localStorage 挖名字；失败回退到 uuid
      const visitorName = resolveVisitorNameFromLS(styleData.data) ?? username
      const tokenName = `&tokenName=${encodeURIComponent(visitorName)}`

      /*
        "customVisitorName": false, // 如果为true，从localStorage里根据jsJsonKey和jsTokenKey获取访客名称
        "tokenDecode": false, // 如果为true，tokenName会被base64解码后再传给后端
        "jsJsonKey": null, // localStorage中的json对象的key，里面包含访客名称
        "jsTokenKey": null, // json对象中存放访客名称的key
      */

      let localParam = this.getLocalFromURL()

      const API_BASE_URL = `${localParam === 'true' ? VITE_LOCAL_API : VITE_API}/auth/oauth2/token`
      const API_ENDPOINT = `${API_BASE_URL}?username=${username}&password=123&grant_type=password&scope=server${tokenName}`

      const headers = {
        'Content-Type': 'application/json',
      }

      if (configId) {
        headers['Authorization'] = `Basic Y2hhdDpjMTIzNDU2`
        headers['TENANT-ID'] = configId
        headers['Accept-Language'] = 'zh_cn'
      }

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: headers,
      })

      if (!response.ok) {
        message.error(t('widget.error.domainUnauthorized'))
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const configData = await response.json()
      console.log('✅ 配置获取成功:', configData)

      // token 按 channelId 命名空间存，避免多渠道互相覆盖
      setChannelToken(configData.access_token, configId)

      // 复用此处已拿到的 styleData，透传给 CustomerWindow，避免其再次调用 getStyleSetting
      return {
        ...this.getDefaultConfig(configId),
        styleSetting: styleData.data ?? null,
      }
    } catch (error) {
      console.warn('❌ 配置获取失败，使用默认配置:', error.message)
      return { ...this.getDefaultConfig(configId), styleSetting: null }
    }
  }

  getDefaultConfig(configId) {
    return {
      configId: configId,
      primaryColor: '#1890ff',
    }
  }

  async initWidget() {
    console.log('🚀 ABot Widget 开始初始化', this.configId)

    // 客服独立页模式：URL 同时携带 token 和 chatId 时走客服分支，
    // 不再执行访客侧的匿名 password grant，也不挂 CustomerWindow。
    const agentParams = this.getAgentModeParams()
    if (agentParams) {
      await this.initAgentMode(agentParams)
      return
    }

    try {
      const userConfig = await this.fetchUserConfig(this.configId)
      const store = createPinia()

      this.app = createApp(CustomerWindow, {
        config: userConfig,
        containerId: this.container.id || `abot-widget-${this.configId}`,
      })

      this.app.use(store)

      try {
        this.app.use(i18nPlugin)
        console.log('✅ i18n 安装成功')
      } catch (error) {
        console.error('❌ i18n 安装失败:', error)
      }

      this.app.mount(this.container)
      console.log('✅ ABot Widget 初始化完成')
    } catch (error) {
      console.error('❌ ABot Widget 初始化失败:', error)
    }
  }

  // 客服模式初始化：
  // 1. URL 参数语义：config → memberId（客服身份 id，非 channelId）
  //                  auth → 登录 token
  //                  chatId → 目标会话 id
  //    真实的 channelId、客户 userId 由 /merchant/chats/userInfo 接口后拉。
  // 2. 挂 Vue 前先把身份（role/memberId/agent token）落到 channelStorage 模块级状态，
  //    这样后续 makeRequest 的 Tenant-Id 自动走 memberId、getToken 命中 agent 命名空间。
  // 3. getChatUserInfo 一并承担 token 校验：接口非 2xx → 视为 token 无效 → 渲染 AgentAuthError。
  //    成功后拿到的 channelId 通过 setChannelId 写入模块级 override，chat store 首次实例化
  //    构造 socket URL 时会读到正确的 channelId（而不是 URL 里的 memberId）。
  // 4. 拉 style（用真实 channelId），挂 AgentWindow，透传 chatId/userId 免得内部再抠。
  async initAgentMode({ token, chatId, agentId }) {
    // 这里的 this.configId 已经被 getConfigFromScript 兜底成 URL 的 ?config 值 = memberId。
    // 变量名保留 configId 是历史包袱；语义上此时是客服的 memberId，命名不改动以减小 diff。
    const memberId = this.configId
    console.log('🧑‍💼 ABot Widget 客服模式初始化', { memberId, chatId })

    setRole('agent')
    setMemberId(memberId)
    setAgentToken(token, memberId)

    // 第一步：调 userInfo，兼任 token 校验 + 拿元信息。
    let chatInfo = null
    try {
      const res = await getChatUserInfo(chatId, memberId)
      const body = await res.json()
      chatInfo = body?.data ?? null
      if (!chatInfo || !chatInfo.channelId) {
        throw new Error('userInfo 响应缺少 channelId')
      }
    } catch (error) {
      console.error('[AgentMode] token 校验或 chatInfo 拉取失败:', error)
      this.mountAgentAuthError()
      return
    }

    // 关键：channelId 就位后立即写入模块级 override，让后续 getChannelId()
    // 与 chat store 构造 socket URL 都能拿到正确值（否则会用 URL 里的 memberId 冒充）。
    setChannelId(chatInfo.channelId)

    // 第二步：拉 style（现在 channelId 是真实的了）。失败不阻断，回退默认样式即可。
    let styleData = null
    try {
      const styleRes = await getStyleSetting(chatInfo.channelId)
      const styleBody = await styleRes.json()
      styleData = styleBody?.data ?? null
    } catch (error) {
      console.warn('[AgentMode] 样式配置获取失败，走默认值:', error)
    }

    try {
      const store = createPinia()

      this.app = createApp(AgentWindow, {
        config: {
          configId: chatInfo.channelId,
          styleSetting: styleData,
        },
        chatId,
        userId: chatInfo.userId ?? '0',
        agentId: agentId ?? '',
      })

      this.app.use(store)

      try {
        this.app.use(i18nPlugin)
      } catch (error) {
        console.error('❌ i18n 安装失败:', error)
      }

      this.app.mount(this.container)
      console.log('✅ ABot Widget 客服模式初始化完成')
    } catch (error) {
      console.error('❌ ABot Widget 客服模式初始化失败:', error)
    }
  }

  mountAgentAuthError() {
    try {
      const store = createPinia()
      this.app = createApp(AgentAuthError)
      this.app.use(store)
      try {
        this.app.use(i18nPlugin)
      } catch (error) {
        console.error('❌ i18n 安装失败:', error)
      }
      this.app.mount(this.container)
    } catch (error) {
      console.error('❌ 渲染客服认证错误页失败:', error)
    }
  }

  disconnectedCallback() {
    if (this.app) {
      this.app.unmount()
    }
  }
}

// 注册 Web Component
if (!customElements.get('abot-widget')) {
  customElements.define('abot-widget', ABotWidget)
}

// 手动初始化方法
window.ABot = {
  init: function (userConfig) {
    const widget = document.createElement('abot-widget')
    if (userConfig.configId) {
      widget.setAttribute('config', userConfig.configId)
    }
    document.body.appendChild(widget)
    console.log('✅ 手动初始化完成')
  },
}

// 自动初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('abot-widget')) {
      const widget = document.createElement('abot-widget')
      document.body.appendChild(widget)
    }
  })
} else {
  if (!document.querySelector('abot-widget')) {
    const widget = document.createElement('abot-widget')
    document.body.appendChild(widget)
  }
}
